import { useEffect } from 'react';
import { sprintf } from 'sprintf-js';

import { Notification } from 'api/types/ApiNotification';
import { Box } from 'components/Box';
import { Link } from 'components/RouterLink';
import { Text } from 'components/Text/Text';
import { UserNotification } from 'components/UserNotification';
import { colors, fontWeights } from 'theme';
import { trackEvent } from 'utils/analytics/track/trackEvent';

import { NotificationContent } from './NotificationContent';

type Props = {
  notifications: Array<Notification>;
  dismiss: (ids: Array<string>) => void;
  open: boolean;
};

export function Notifications({ notifications, dismiss, open }: Props) {
  const primaryNotifications = notifications.slice(0, 3);
  const otherNotifications =
    notifications.length > 3 ? notifications.slice(3) : null;
  const otherListingApplicationNotifications = otherNotifications
    ? otherNotifications.filter(
        (notification) => notification.type === 'NEW_APPLICATIONS_BY_LISTING'
      )
    : null;
  const otherListingApplicationData =
    otherListingApplicationNotifications &&
    otherListingApplicationNotifications.length >= 1 &&
    otherListingApplicationNotifications[0].type ===
      'NEW_APPLICATIONS_BY_LISTING'
      ? otherListingApplicationNotifications[0].data
      : null;
  const otherApplicationMentionNotifications = otherNotifications
    ? otherNotifications.filter(
        (notification) => notification.type === 'NEW_MENTIONS_BY_APPLICATION'
      )
    : null;
  const otherApplicationMentionData =
    otherApplicationMentionNotifications &&
    otherApplicationMentionNotifications.length >= 1 &&
    otherApplicationMentionNotifications[0].type ===
      'NEW_MENTIONS_BY_APPLICATION'
      ? otherApplicationMentionNotifications[0].data
      : null;
  const otherNewSuggestedApplicantsNotifications = otherNotifications
    ? otherNotifications.filter(
        (notification) =>
          notification.type === 'NEW_SUGGESTED_APPLICANTS_BY_JOB'
      )
    : null;
  const otherNewSuggestedApplicantsData =
    otherNewSuggestedApplicantsNotifications &&
    otherNewSuggestedApplicantsNotifications.length >= 1 &&
    otherNewSuggestedApplicantsNotifications[0].type ===
      'NEW_SUGGESTED_APPLICANTS_BY_JOB'
      ? otherNewSuggestedApplicantsNotifications[0].data
      : null;

  useEffect(() => {
    if (open && notifications.length) {
      trackEvent('Displayed User Menu Notifications', {
        notification_types: notifications.reduce(
          (acc, notification) =>
            // @ts-expect-error TS(2769): No overload matches this call.
            acc.includes(notification.type) ? acc : [...acc, notification.type],
          []
        ),
      });
    }
  }, [notifications, open]);
  return (
    <>
      {primaryNotifications.map((notification) => {
        const dismissNotification = () => {
          let ids = [];

          if (notification.type === 'NEW_APPLICATIONS_BY_LISTING') {
            ids = notification.data.applications.map(
              (application) => application.notificationId
            );
          } else if (notification.type === 'NEW_MENTIONS_BY_APPLICATION') {
            ids = notification.data.mentions.map(
              (mention) => mention.notificationId
            );
          } else if (notification.type === 'NEW_SUGGESTED_APPLICANTS_BY_JOB') {
            ids = notification.data.batches.map(
              (batch) => batch.notificationId
            );
          } else {
            ids = [notification.id];
          }

          dismiss(ids);
        };

        return (
          <Box
            p={12}
            minWidth={260}
            borderTop={`1px solid ${colors.selectionGrey}`}
            key={
              notification.type === 'NEW_APPLICATIONS_BY_LISTING' ||
              notification.type === 'NEW_MENTIONS_BY_APPLICATION' ||
              notification.type === 'NEW_SUGGESTED_APPLICANTS_BY_JOB'
                ? notification.data.id
                : notification.id
            }
          >
            <UserNotification dismiss={dismissNotification}>
              <NotificationContent
                notification={notification}
                dismiss={dismissNotification}
              />
            </UserNotification>
          </Box>
        );
      })}
      {otherListingApplicationNotifications &&
        otherListingApplicationNotifications.length >= 1 &&
        otherListingApplicationData && (
          <Box
            p={12}
            minWidth={260}
            borderTop={`1px solid ${colors.selectionGrey}`}
          >
            <Link
              to={`/admin/${otherListingApplicationData.orgId}${
                otherListingApplicationNotifications.length === 1
                  ? `/${otherListingApplicationData.type.toLowerCase()}s/${
                      otherListingApplicationData.id
                    }`
                  : ''
              }`}
            >
              <Text fontWeight={fontWeights.semiBold}>
                {sprintf(
                  otherListingApplicationNotifications.length > 1
                    ? getText(
                        '%(count)s more listings with unread applications'
                      )
                    : getText(
                        '%(count)s more listing with unread applications'
                      ),
                  {
                    count: otherListingApplicationNotifications.length,
                  }
                )}
              </Text>
            </Link>
          </Box>
        )}

      {otherApplicationMentionNotifications &&
        otherApplicationMentionNotifications.length >= 1 &&
        otherApplicationMentionData && (
          <Box
            p={12}
            minWidth={260}
            borderTop={`1px solid ${colors.selectionGrey}`}
          >
            <Link
              to={`/admin/${otherApplicationMentionData.orgId}${
                otherApplicationMentionNotifications.length === 1
                  ? `/applications/${otherApplicationMentionData.id}`
                  : ''
              }`}
            >
              <Text fontWeight={fontWeights.semiBold}>
                {sprintf(
                  otherApplicationMentionNotifications.length > 1
                    ? getText(
                        '%(count)s more applications with unread mentions'
                      )
                    : getText(
                        '%(count)s more application with unread mentions'
                      ),
                  {
                    count: otherApplicationMentionNotifications.length,
                  }
                )}
              </Text>
            </Link>
          </Box>
        )}

      {otherNewSuggestedApplicantsNotifications &&
        otherNewSuggestedApplicantsNotifications.length >= 1 &&
        otherNewSuggestedApplicantsData && (
          <Box
            p={12}
            minWidth={260}
            borderTop={`1px solid ${colors.selectionGrey}`}
          >
            <Link
              to={`/admin/${otherNewSuggestedApplicantsData.orgId}${
                otherNewSuggestedApplicantsNotifications.length === 1
                  ? `/jobs/${otherNewSuggestedApplicantsData.job.id}/recommended-candidates`
                  : ''
              }`}
            >
              <Text fontWeight={fontWeights.semiBold}>
                {sprintf(
                  otherNewSuggestedApplicantsNotifications.length > 1
                    ? getText(
                        '%(count)s more listings with recommended candidates'
                      )
                    : getText(
                        '%(count)s more listing with recommended candidates'
                      ),
                  {
                    count: otherNewSuggestedApplicantsNotifications.length,
                  }
                )}
              </Text>
            </Link>
          </Box>
        )}
    </>
  );
}
